import React from 'react'
import Reveal3 from '@/components/marketing3/Reveal3'
import AgentConsole from '@/components/marketing3/widgets/AgentConsole'
import McpRail from '@/components/marketing3/widgets/McpRail'

/**
 * AiNative3 — the second "room of night": a dark, cinematic AI-native section, the
 * page's flex. Eyebrow + H2 + three pillars (machine-readable explainability;
 * connect your agents via MCP; co-pilot → autopilot — this section is the home of
 * the co-pilot/autopilot vocabulary), with the AgentConsole (W10) + MCP rail as the
 * hero artifact.
 *
 * Copy is quoted VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §AI-native
 * ({BRAND} → the `brand` prop). The §0 do-not-publish list applies to every string,
 * including comments and aria: no PMS name, no stage counts, no cadence, no
 * verification claim, no competitor names, and — critically for this section — no
 * not-yet-available / roadmap-hedge language anywhere (the copy doc's as-fact
 * register governs; the mobile chip carries the only such wording, on its own).
 *
 * This dark room no longer full-bleeds between its light neighbours: it's now a giant
 * rounded dark instrument panel resting ON the light page — the big sibling of the
 * Targets gauge (mkt3-panel-dark.mkt3-panel-hot), the page's largest instrument. The
 * outer <section> sits on the normal light bg with standard vertical rhythm; the inner
 * wrapper carries mkt3-dark (so every dark-context cascade keeps working on the content
 * inside) plus the rounded night surface, the mkt3-dark-glow + grid atmosphere, a white
 * hairline border and a soft teal halo. No seams, no gradient fades.
 */

interface Pillar {
  title: string
  body: React.ReactNode
}

/** The three pillars — heading + body, verbatim from the copy doc §AI-native. */
const PILLARS: Pillar[] = [
  {
    title: 'Explainable = machine-readable.',
    body: 'Every price decision is structured, explainable data an AI can read, audit and act on — not a black box even a human can’t inspect.',
  },
  {
    title: 'Connect your agents.',
    body: 'Connect Claude, Codex, or your own agent through one connection, and let it read your whole pricing picture — calendar, targets, guardrails — in a single move.',
  },
  {
    title: 'Co-pilot to autopilot.',
    body: (
      <>
        Set every rule and guardrail by hand if you like. Or plan with the agent — &ldquo;August looks soft after
        the 15th, what&rsquo;s the response?&rdquo; — approve its plan, and let it execute inside the guardrails you
        set. You approve; it applies.
      </>
    ),
  },
]

export default function AiNative3({ brand }: { brand: string }) {
  return (
    <section id="ai" className="scroll-mt-24" aria-label={`${brand} and AI agents`}>
      <div className="mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-28">
        {/* The inset dark instrument panel — the page's largest "instrument on paper".
            mkt3-dark rides on this same element so every dark-context cascade works on
            the content inside; the panel paints its own opaque night surface with a
            rounded frame, hairline border and teal halo. */}
        <div className="mkt3-dark mkt3-panel-inset relative isolate overflow-hidden rounded-[1.75rem] p-6 sm:p-10 lg:p-14">
          {/* Atmosphere — the single teal light source + faint blueprint grid, contained
              INSIDE the panel by overflow-hidden + rounding */}
          <div aria-hidden="true" className="mkt3-dark-glow pointer-events-none absolute inset-0" />
          <div aria-hidden="true" className="mkt3-grid pointer-events-none absolute inset-0" />

          <div className="relative">
            {/* Section header */}
            <Reveal3 className="max-w-3xl">
              <p className="mkt3-eyebrow">AI-native</p>
              <h2 className="mkt3-display mkt3-h2 mt-4">Run it by hand. Or run it with an agent.</h2>
            </Reveal3>

            {/* Two columns: prose (three pillars) + the hero artifact console */}
            <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_1.05fr] lg:items-start lg:gap-16">
              {/* Prose column — the three pillars */}
              <Reveal3 className="lg:pt-2" delay={60}>
                <ul className="space-y-9">
                  {PILLARS.map((pillar, i) => (
                    <li key={pillar.title} className="relative pl-7">
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-[0.15em] font-mono text-[11px] tabular-nums text-teal-400/70"
                      >
                        0{i + 1}
                      </span>
                      <h3 className="mkt3-h3 mkt3-display not-italic font-semibold text-slate-100">{pillar.title}</h3>
                      <p className="mkt3-prose mt-2">{pillar.body}</p>
                    </li>
                  ))}
                </ul>
              </Reveal3>

              {/* Hero artifact — the agent console + the MCP rail beneath it */}
              <Reveal3 delay={120}>
                <AgentConsole />
                <McpRail className="mt-5 px-1" />
              </Reveal3>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
